import { CSSProperties, useCallback, useMemo, useRef } from "react";
import { Cell, Row } from "react-aria-components";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableRow from "components/Table/Row";
import TableWrapper from "components/Table/Wrapper";
import useTableSort from "components/Table/useTableSort";
import useTitle from "hooks/useTitle";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import Button from "ds/components/Button";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useCanManageUsers from "shared/User/access/useCanManageUsers";

import IntegrationsListItem from "./ListItem";
import {
  ANALYTICS,
  FILTERS_ORDER_SETTINGS_KEY,
  INITIAL_COLUMNS_VIEW_CONFIG,
  initialSortDirection,
  initialSortOption,
  ROW_HEADER_ID,
  SORT_CONFIG,
  USER_INTEGRATIONS_COLUMN_CONFIG,
  USER_INTEGRATIONS_COLUMNS_STORAGE_KEY,
  USER_INTEGRATIONS_CUSTOM_END_COLUMN_CONFIG,
} from "./constants";
import { Integration } from "./types";
import { useFilteredEntities } from "./useFilteredEntities";
import UserIntegrationsEmpty from "./Empty";
import { UserViewDetails } from "../types";
import { FILTERS_ORDER_SETTINGS_KEY as USER_FILTERS_ORDER_SETTINGS_KEY } from "../../Users/ListNew/constants";
import { showSlackIntegration } from "./SlackIntegrationDrawer";
type UserIntegrationsProps = {
  user: UserViewDetails;
};

const UserIntegrations = ({ user }: UserIntegrationsProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);
  const canManageUsers = useCanManageUsers();
  const { handleSortOptionChange, sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FILTERS_ORDER_SETTINGS_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption,
    initialSortDirection,
  });

  // For now we only can have one single integration - Slack
  const integrations = useMemo(() => {
    if (user.slackMemberID) {
      return [{ id: user.slackMemberID, type: "Slack" } as Integration];
    }

    return [];
  }, [user.slackMemberID]);

  const filteredIntegrations = useFilteredEntities(integrations, sortDescriptor);

  const isPageEmpty = filteredIntegrations.length === 0;

  useTitle(`Organization Settings · Users · ${user.username} · Integrations`);

  useBreadcrumbs(
    [
      {
        title: "Organization Settings",
      },
      {
        title: "Users",
        link: getBreadcrumbsBackUrl("/settings/users", USER_FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: user.username,
      },
    ],
    []
  );

  const renderRow = useCallback(
    (
      item: Partial<Integration> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      if ("height" in item && (item.id === "after" || item.id === "before")) {
        return (
          <Row>
            <Cell style={{ height: item.height }} />
          </Row>
        );
      }

      return (
        <TableRow
          index={item.virtIndex}
          ref={item.ref}
          style={item.style}
          key={item.virtKey}
          id={item.id}
          name={item.id}
        >
          <IntegrationsListItem integration={item as Integration} user={user} />
        </TableRow>
      );
    },
    [user]
  );

  return (
    <>
      <PageInfoHeader title="Integrations">
        {!!integrations?.length && (
          <SearchInput
            placeholder="Search..."
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY}
          />
        )}

        {canManageUsers && (
          <Button
            variant="primary"
            analyticsPage={AnalyticsPageOrganization.OrganizationUserIntegrations}
            analyticsTitle="Add Integration Clicked"
            onPress={() => showSlackIntegration({ user })}
          >
            Add integration
          </Button>
        )}
      </PageInfoHeader>
      <TableContextProvider
        columnsConfig={USER_INTEGRATIONS_COLUMN_CONFIG}
        endCustomColumns={USER_INTEGRATIONS_CUSTOM_END_COLUMN_CONFIG}
        initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
        rowHeaderId={ROW_HEADER_ID}
        localStorageId={USER_INTEGRATIONS_COLUMNS_STORAGE_KEY}
        setSortDescriptor={handleSortOptionChange}
        sortDescriptor={sortDescriptor}
        {...ANALYTICS}
      >
        {!!filteredIntegrations.length && (
          <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
            <Table
              ariaLabel="Integrations"
              items={filteredIntegrations}
              ref={virtualizedListContainerRef}
            >
              {renderRow}
            </Table>
          </TableWrapper>
        )}
        {!integrations?.length && <UserIntegrationsEmpty />}
        {isPageEmpty && !!integrations?.length && (
          <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
        )}
      </TableContextProvider>
    </>
  );
};

export default UserIntegrations;
